/** Optional AI-first intent routing (when user enables Smart AI mode). */

import { askLocalLlm, isLlmEnabled } from "assistant/llmClient";
import { extractJsonObject } from "assistant/llmClientUtils";
import { draftFromClassifiedIntent } from "assistant/commandFlow";

export async function classifyRouteIntent(message, context) {
  if (!context?.aiRoutingEnabled || !isLlmEnabled()) {
    return { ok: false, error: "disabled" };
  }

  const lastTeacher = context.lastTeacher;
  const contextHint = lastTeacher
    ? {
        lastTeacher: {
          id: lastTeacher.id || null,
          name: lastTeacher.name || null,
          reg: lastTeacher.reg || null,
        },
      }
    : null;

  const result = await askLocalLlm(message, context, {
    mode: "route",
    ruleFacts: {
      intent: "route",
      structuredText: contextHint
        ? `Last teacher in conversation: ${lastTeacher.name || "?"} (${lastTeacher.reg || "no reg"})`
        : "No teacher selected in conversation yet.",
    },
  });

  if (!result?.ok) return { ok: false, ...result };

  const parsed = extractJsonObject(result.reply);
  if (!parsed || typeof parsed !== "object") {
    return { ok: false, error: "parse", detail: "Router returned non-JSON" };
  }

  return { ok: true, route: normalizeRoute(parsed) };
}

function normalizeRoute(raw) {
  const intent = String(raw.intent || raw.id || "none").trim();
  const confidence = Number(raw.confidence ?? raw.score ?? 0);
  return {
    intent,
    confidence: Number.isFinite(confidence) ? confidence : 0,
    useLastTeacher: Boolean(raw.useLastTeacher ?? raw.use_last_teacher),
    teacherQuery: String(raw.teacherQuery || raw.teacher_query || "").trim() || null,
    establishmentQuery: String(raw.establishmentQuery || raw.establishment_query || "").trim() || null,
    writeCommand: String(raw.writeCommand || raw.command || "").trim() || null,
    writeHints: raw.writeHints || raw.hints || {},
  };
}

export function isActionableRoute(route) {
  if (!route || route.intent === "none" || route.intent === "unknown") return false;
  if (route.confidence < 0.55) return false;
  return true;
}

/** Build write-command draft from an AI route (reuses classify shape). */
export function writeDraftFromRoute(route) {
  if (!route?.writeCommand) return null;
  return draftFromClassifiedIntent({
    kind: "command",
    command: route.writeCommand,
    confidence: route.confidence,
    hints: route.writeHints,
  });
}
